if (!customElements.get("shoppable-drawer")) {
  const sectionStates = new WeakMap();
  const initializedDialogs = new WeakSet();

  const hideSectionTooltips = (section) => {
    section.querySelectorAll(".wt-dot__tooltip").forEach((tooltip) => {
      tooltip.classList.remove("wt-dot__tooltip--active");
      tooltip.setAttribute("aria-hidden", "true");
      tooltip.setAttribute("inert", "");
    });

    section.querySelectorAll("shoppable-drawer").forEach((drawer) => {
      drawer.querySelector(".wt-dot")?.classList.remove("wt-dot--active");
      drawer
        .querySelector(".wt-dot__circle")
        ?.setAttribute("aria-expanded", "false");
    });
  };

  const getSectionState = (section) => {
    let state = sectionStates.get(section);
    if (state) return state;

    state = {
      activeDrawer: null,
      drawers: new Set(),
      handleDocumentClick: (event) => {
        if (window.matchMedia("(max-width: 899px)").matches) return;

        const clickedDrawer = event.target?.closest?.("shoppable-drawer");
        if (clickedDrawer && section.contains(clickedDrawer)) return;

        hideSectionTooltips(section);
      },
    };

    sectionStates.set(section, state);
    document.addEventListener("click", state.handleDocumentClick);
    return state;
  };

  const releaseSectionState = (section, drawer) => {
    const state = sectionStates.get(section);
    if (!state) return;

    state.drawers.delete(drawer);
    if (state.activeDrawer === drawer) state.activeDrawer = null;
    if (state.drawers.size) return;

    document.removeEventListener("click", state.handleDocumentClick);
    sectionStates.delete(section);
  };

  customElements.define(
    "shoppable-drawer",
    class ShoppableDrawer extends HTMLElement {
      connectedCallback() {
        if (this.isConnectedAndReady) return;

        this.section = this.closest(".shoppable-image");
        this.button = this.querySelector(".wt-dot__circle");
        this.hotspot = this.querySelector(".wt-dot");
        this.tooltip = this.findScopedElementById(
          this.button?.dataset.tooltipId,
          ".wt-dot__tooltip",
          this,
        );
        this.wrapper = this.findScopedElementById(
          this.button?.dataset.dialogId,
          ".wt__shoppable-image--wrapper",
          this.section,
        );
        this.pageOverlay = this.section?.querySelector(
          ".wt__shoppable-image--page-overlay",
        );
        this.content = this.wrapper?.querySelector(
          ".wt__shoppable-image--content",
        );
        this.closeButton = this.wrapper?.querySelector(".wt-options__close");
        this.mobileQuery = window.matchMedia("(max-width: 899px)");

        if (!this.section || !this.button || !this.tooltip) return;

        this.sectionState = getSectionState(this.section);
        this.sectionState.drawers.add(this);
        this.isOpen = false;
        this.button.setAttribute("aria-expanded", "false");
        this.tooltip.setAttribute("aria-hidden", "true");
        this.tooltip.setAttribute("inert", "");
        this.syncTriggerRelationship();
        this.initializeDialog();

        this.button.addEventListener("click", this.handleTriggerClick);
        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener("change", this.handleMediaChange);
        } else {
          this.mobileQuery.addListener(this.handleMediaChange);
        }
        this.isConnectedAndReady = true;
      }

      findScopedElementById(id, fallbackSelector, scope) {
        if (!scope) return null;
        if (id) {
          const match = Array.from(scope.querySelectorAll("[id]")).find(
            (element) => element.id === id,
          );
          if (match) return match;
        }
        return scope.querySelector(fallbackSelector);
      }

      initializeDialog() {
        if (!this.wrapper || initializedDialogs.has(this.wrapper)) return;

        this.wrapper.setAttribute("role", "dialog");
        this.wrapper.setAttribute("aria-modal", "true");
        this.wrapper.setAttribute("aria-hidden", "true");
        this.wrapper.setAttribute("inert", "");
        if (!this.wrapper.hasAttribute("tabindex")) {
          this.wrapper.setAttribute("tabindex", "-1");
        }
        this.wrapper.classList.remove("open");
        this.wrapper.removeAttribute("open");
        this.pageOverlay?.classList.add("hidden");
        this.pageOverlay?.setAttribute("aria-hidden", "true");
        this.pageOverlay?.setAttribute("inert", "");
        this.content?.replaceChildren();
        initializedDialogs.add(this.wrapper);
      }

      syncTriggerRelationship() {
        const controlledId = this.mobileQuery.matches
          ? this.wrapper?.id || this.button.dataset.dialogId
          : this.tooltip?.id || this.button.dataset.tooltipId;

        if (controlledId) {
          this.button.setAttribute("aria-controls", controlledId);
        }
      }

      handleTriggerClick = (event) => {
        event.preventDefault();
        if (this.mobileQuery.matches) {
          this.openDialog();
        } else {
          this.toggleTooltip();
        }
      };

      toggleTooltip() {
        const wasActive = this.tooltip.classList.contains(
          "wt-dot__tooltip--active",
        );
        hideSectionTooltips(this.section);
        if (wasActive) return;

        this.tooltip.classList.add("wt-dot__tooltip--active");
        this.tooltip.setAttribute("aria-hidden", "false");
        this.tooltip.removeAttribute("inert");
        this.hotspot?.classList.add("wt-dot--active");
        this.button.setAttribute("aria-expanded", "true");
      }

      openDialog() {
        if (
          !this.wrapper ||
          !this.pageOverlay ||
          !this.content ||
          !this.closeButton
        ) {
          return;
        }

        if (this.isOpen) {
          this.closeDialog();
          return;
        }

        const productContent = this.tooltip.querySelector(".wt-dot__link");
        if (!productContent) return;

        const activeDrawer = this.sectionState.activeDrawer;
        window.wtDocumentScrollLock.lock(this);
        if (activeDrawer && activeDrawer !== this) {
          activeDrawer.closeDialog({ restoreFocus: false });
        }

        this.sectionState.activeDrawer = this;
        this.returnFocusElement = this.button;
        this.isOpen = true;
        hideSectionTooltips(this.section);
        this.hotspot?.classList.add("wt-dot--active");
        this.button.setAttribute("aria-expanded", "true");

        this.content.replaceChildren(productContent.cloneNode(true));
        this.pageOverlay.classList.remove("hidden");
        this.pageOverlay.setAttribute("aria-hidden", "false");
        this.pageOverlay.removeAttribute("inert");
        this.wrapper.classList.add("open");
        this.wrapper.setAttribute("open", "");
        this.wrapper.setAttribute("aria-hidden", "false");
        this.wrapper.removeAttribute("inert");

        this.closeButton.addEventListener("click", this.handleCloseClick);
        this.pageOverlay.addEventListener("click", this.handleBackdropClick);
        document.addEventListener("keydown", this.handleDialogKeydown);

        this.focusFrame = requestAnimationFrame(() => {
          if (!this.isOpen) return;
          (this.closeButton || this.wrapper).focus({ preventScroll: true });
        });
      }

      closeDialog({ restoreFocus = true } = {}) {
        if (!this.isOpen) return;

        this.isOpen = false;
        cancelAnimationFrame(this.focusFrame);
        this.closeButton?.removeEventListener("click", this.handleCloseClick);
        this.pageOverlay?.removeEventListener(
          "click",
          this.handleBackdropClick,
        );
        document.removeEventListener("keydown", this.handleDialogKeydown);

        this.wrapper?.classList.remove("open");
        this.wrapper?.removeAttribute("open");
        this.wrapper?.setAttribute("aria-hidden", "true");
        this.wrapper?.setAttribute("inert", "");
        this.pageOverlay?.classList.add("hidden");
        this.pageOverlay?.setAttribute("aria-hidden", "true");
        this.pageOverlay?.setAttribute("inert", "");
        this.content?.replaceChildren();
        this.hotspot?.classList.remove("wt-dot--active");
        this.button?.setAttribute("aria-expanded", "false");

        if (this.sectionState?.activeDrawer === this) {
          this.sectionState.activeDrawer = null;
        }
        window.wtDocumentScrollLock.unlock(this);

        const focusTarget = this.returnFocusElement;
        this.returnFocusElement = null;
        if (restoreFocus && focusTarget?.isConnected) {
          requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
        }
      }

      handleCloseClick = (event) => {
        event.preventDefault();
        this.closeDialog();
      };

      handleBackdropClick = (event) => {
        if (event.target === this.pageOverlay) this.closeDialog();
      };

      handleDialogKeydown = (event) => {
        if (!this.isOpen) return;
        if (event.key === "Escape") {
          event.preventDefault();
          this.closeDialog();
          return;
        }
        if (event.key !== "Tab") return;

        const focusableElements = this.getFocusableElements();
        if (!focusableElements.length) {
          event.preventDefault();
          this.wrapper.focus({ preventScroll: true });
          return;
        }

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;
        if (!this.wrapper.contains(activeElement)) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };

      getFocusableElements() {
        const selector = [
          "a[href]",
          "button:not([disabled])",
          "input:not([disabled]):not([type='hidden'])",
          "select:not([disabled])",
          "textarea:not([disabled])",
          "[contenteditable='true']",
          "[tabindex]:not([tabindex='-1'])",
        ].join(",");

        return Array.from(this.wrapper.querySelectorAll(selector)).filter(
          (element) =>
            element.getAttribute("aria-hidden") !== "true" &&
            !element.closest("[inert]") &&
            element.getClientRects().length > 0,
        );
      }

      handleMediaChange = (event) => {
        this.syncTriggerRelationship();
        hideSectionTooltips(this.section);
        if (!event.matches) this.closeDialog();
      };

      disconnectedCallback() {
        if (!this.isConnectedAndReady) return;

        this.closeDialog({ restoreFocus: false });
        this.button?.removeEventListener("click", this.handleTriggerClick);
        if (this.mobileQuery?.removeEventListener) {
          this.mobileQuery.removeEventListener("change", this.handleMediaChange);
        } else {
          this.mobileQuery?.removeListener(this.handleMediaChange);
        }
        releaseSectionState(this.section, this);
        this.isConnectedAndReady = false;
      }
    },
  );
}

if (!customElements.get("shoppable-image-autoplay")) {
  customElements.define(
    "shoppable-image-autoplay",
    class ShoppableImageAutoplay extends HTMLElement {
      connectedCallback() {
        this.section = this.closest(".shoppable-image");
        this.imageBody = this.section?.querySelector(".shoppable-image__body");
        if (!this.imageBody) return;

        this.items = Array.from(
          this.imageBody.querySelectorAll("[data-block-id]"),
        )
          .map((block) => ({
            dot: block.querySelector(".wt-dot"),
            tooltip: block.querySelector(".wt-dot__tooltip"),
            trigger: block.querySelector(".wt-dot__circle"),
          }))
          .filter((item) => item.dot && item.tooltip && item.trigger);
        if (this.items.length < 2) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
          return;

        this.dwell = parseInt(this.dataset.dwell, 10) || 4000;
        this.gap = 600;
        this.index = -1;
        this.phase = "closed";
        this.timer = null;
        this.inView = false;
        this.hovered = false;
        this.stopped = false;
        this.desktopQuery = window.matchMedia("(min-width: 900px)");

        this.desktopQuery.addEventListener("change", this.handleMediaChange);
        this.imageBody.addEventListener("mouseenter", this.handleMouseEnter);
        this.imageBody.addEventListener("mouseleave", this.handleMouseLeave);
        this.imageBody.addEventListener("click", this.handleInteraction);

        this.observer = new IntersectionObserver(this.handleIntersection, {
          threshold: 0.3,
        });
        this.observer.observe(this.imageBody);
      }

      handleIntersection = (entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          if (this.stopped) return;
          if (this.inView) {
            this.schedule();
          } else {
            this.reset();
          }
        });
      };

      handleMediaChange = (event) => {
        if (this.stopped) return;
        if (event.matches) {
          this.schedule();
        } else {
          this.reset();
        }
      };

      handleMouseEnter = () => {
        this.hovered = true;
        this.halt();
      };

      handleMouseLeave = () => {
        this.hovered = false;
        this.schedule();
      };

      // A visitor opening a hotspot themselves takes over: stop for good.
      handleInteraction = (event) => {
        if (
          event.target.closest(".wt-dot") ||
          event.target.closest(".wt-dot__tooltip")
        ) {
          this.stopped = true;
          this.halt();
        }
      };

      schedule() {
        if (
          this.timer ||
          this.stopped ||
          !this.inView ||
          this.hovered ||
          !this.desktopQuery.matches
        )
          return;
        this.timer = setTimeout(
          () => this.step(),
          this.phase === "open" ? this.dwell : this.gap,
        );
      }

      step() {
        this.timer = null;
        if (this.phase === "closed") {
          this.index = (this.index + 1) % this.items.length;
          this.activate(this.items[this.index]);
          this.phase = "open";
        } else {
          this.deactivateAll();
          this.phase = "closed";
        }
        this.schedule();
      }

      activate(item) {
        this.deactivateAll();
        item.tooltip.classList.add("wt-dot__tooltip--active");
        item.tooltip.setAttribute("aria-hidden", "false");
        item.tooltip.removeAttribute("inert");
        item.dot.classList.add("wt-dot--active");
        item.trigger.setAttribute("aria-expanded", "true");
      }

      deactivateAll() {
        this.items.forEach((item) => {
          item.tooltip.classList.remove("wt-dot__tooltip--active");
          item.tooltip.setAttribute("aria-hidden", "true");
          item.tooltip.setAttribute("inert", "");
          item.dot.classList.remove("wt-dot--active");
          item.trigger.setAttribute("aria-expanded", "false");
        });
      }

      halt() {
        clearTimeout(this.timer);
        this.timer = null;
      }

      reset() {
        this.halt();
        this.deactivateAll();
        this.phase = "closed";
      }

      disconnectedCallback() {
        this.halt();
        this.observer?.disconnect();
        this.desktopQuery?.removeEventListener(
          "change",
          this.handleMediaChange,
        );
        this.imageBody?.removeEventListener(
          "mouseenter",
          this.handleMouseEnter,
        );
        this.imageBody?.removeEventListener(
          "mouseleave",
          this.handleMouseLeave,
        );
        this.imageBody?.removeEventListener("click", this.handleInteraction);
      }
    },
  );
}
